import { useEffect } from "react";
import { Activity, AlertTriangle, Power } from "lucide-react";
import { usePsychAssistHeartbeat } from "../../hooks/usePsychAssistHeartbeat";
import { usePsychAssistSession } from "../../hooks/usePsychAssistSession";
import { PsychAssistHeartline } from "./PsychAssistHeartline";
import { FintheonNumericText } from "../numbers/FintheonNumericText";

interface CompactERMonitorProps {
  onERScoreChange?: (score: number) => void;
  compact?: boolean;
}

const levelLabels = {
  unknown: "Unknown",
  steady: "Steady",
  elevated: "Elevated",
  high: "High",
  critical: "Critical",
};

const levelDot = {
  unknown: "bg-[var(--fintheon-text)]/20",
  steady: "bg-[var(--fintheon-accent)]",
  elevated: "bg-[var(--fintheon-accent)]/70",
  high: "bg-[var(--fintheon-accent)]/50",
  critical: "bg-[var(--fintheon-accent)]/35",
};

export function CompactERMonitor({
  onERScoreChange,
  compact = false,
}: CompactERMonitorProps) {
  const heartbeat = usePsychAssistHeartbeat();
  const psych = usePsychAssistSession();
  const isActive = psych.isActive;
  const score = psych.currentScore;
  const level = psych.currentLevel;
  const scoreLabel = score === null ? "--" : score.toFixed(1);
  const needsCaution = level === "high" || level === "critical";

  useEffect(() => {
    if (score === null) return;
    onERScoreChange?.(score);
    window.dispatchEvent(
      new CustomEvent("psychassist:score", {
        detail: {
          score,
          level,
          timestamp: Date.now(),
          source: psych.session?.opening.sourceProvider ?? "wearable",
        },
      }),
    );
  }, [level, onERScoreChange, psych.session, score]);

  const toggle = () => {
    if (isActive) void psych.finalize("user_stopped");
    else void psych.start();
  };

  return (
    <div className="flex w-full items-center gap-2 py-1">
      <PsychAssistHeartline
        status={heartbeat.status}
        loading={heartbeat.loading || psych.syncing}
        isMonitoring={isActive}
        isLockedOut={needsCaution}
      />

      <div className="flex shrink-0 items-center gap-1.5">
        <div
          className={`h-1.5 w-1.5 rounded-full ${levelDot[level]} ${
            isActive ? "animate-pulse" : ""
          }`}
        />
        <span className="text-xs font-bold text-[var(--fintheon-accent)]">
          <FintheonNumericText kind="score" text={scoreLabel} />
        </span>
        {!compact && (
          <span className="text-[9px] text-[var(--fintheon-text)]/45">
            <FintheonNumericText
              kind="duration"
              text={
                psych.retrySeconds
                  ? `${psych.retrySeconds}s`
                  : levelLabels[level]
              }
            />
          </span>
        )}
        {!compact && needsCaution && (
          <AlertTriangle className="h-3 w-3 text-[var(--fintheon-accent)]/70" />
        )}
      </div>

      <button
        onClick={toggle}
        className={`toolbar-icon-btn shrink-0 transition-colors ${
          isActive
            ? "!bg-[var(--fintheon-accent)]/10 !text-[var(--fintheon-accent)]"
            : ""
        }`}
        title={isActive ? "Stop PsychAssist" : "Start PsychAssist"}
      >
        {isActive ? (
          <Power className="h-3 w-3" />
        ) : (
          <Activity className="h-3 w-3" />
        )}
      </button>
    </div>
  );
}
